import Box from "@mui/material/Box";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import { useDialog } from 'app/providers/dialog';
import { RootState, useAttorneyHubDispatch } from 'app/realtime-store/redux-store';
import Guid from 'common/values/guid/guid';
import LegalEntityAPIService from 'legal-entities/entities/legal-entity/api/legal-entity-api-service';
import LegalEntity from 'legal-entities/entities/legal-entity/legal-entity';
import { getUserLegalEntitiesByUserId, populateLegalEntities } from 'legal-entities/entities/legal-entity/store/legal-entity-redux-slice';
import EditLegalEntity from 'legal-entities/entities/legal-entity/view/edit-legal-entity';
import {
  createMRTColumnHelper, MaterialReactTable, MRT_ColumnFiltersState,
  MRT_PaginationState, MRT_SortingState, MRT_TableInstance, useMaterialReactTable
} from 'material-react-table';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router';
import { useSession } from 'users/session/session-context';

const NameCellContainer = styled(Box)(() => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
}));
const NameText = styled(ListItemText)(({ theme }) => ({
  marginLeft: theme.spacing(1)
}));

interface LegalEntityAdministrationTableProps {
  renderTopToolbarCustomActions: (props: { table: MRT_TableInstance<LegalEntity> }) => React.ReactNode;
}

const LegalEntityAdministrationTable: React.FC<LegalEntityAdministrationTableProps> = ({ renderTopToolbarCustomActions }) => {
  
  const [query, setQuery] = React.useState("");
  const [data, setData] = React.useState<LegalEntity[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const navigate = useNavigate();
  const session = useSession();
  const { openDialog } = useDialog();
  const snackbar = useSnackbar();

  const userId = session.user?.id?.value;
  const reduxError = useSelector((state: RootState) => userId ? state.legalEntities?.error[userId] : null);
  const wasErrorLoading = !!reduxError;
  const [columnFilters, setColumnFilters] =
    React.useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<MRT_SortingState>([]);
  const [pagination, setPagination] = React.useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const legalEntityResponse = session.user?.id
    ? getUserLegalEntitiesByUserId(session.user.id)
    : [];
    
  const dispatch = useAttorneyHubDispatch();
  const reduxLoading = useSelector((state: RootState) => userId ? state.legalEntities?.loading[userId] : false);

  const isLoading = !legalEntityResponse && reduxLoading;
  const isRefetching = !!legalEntityResponse && reduxLoading;

  useEffect(() => {
    if (!session.user?.companyEntityId || !session.user?.isCompanyManager) return;
    if (session.user?.id && !legalEntityResponse) {
      dispatch(populateLegalEntities({ session: session, userId: session.user.id }));
    }
  }, [
    dispatch,
    session.user?.companyEntityId,
    session.user?.isCompanyManager,
    session.user?.id,
    legalEntityResponse
  ]);

  useEffect(() => {
    if (legalEntityResponse) {
      setData(legalEntityResponse);
      setRowCount(legalEntityResponse.length);
    }
  }, [legalEntityResponse]);


  async function updateLegalEntity(updated: LegalEntity): Promise<LegalEntity | null> {
    const abortController = new AbortController();
    try {
      const legalEntityService = new LegalEntityAPIService(session);
      const original = data.find(e => e.id.value === updated.id.value);
      if (!original) return null;
      return await legalEntityService.updateLegalEntity(original, updated, abortController);
    } catch (error) {
      console.error(error);
      abortController.abort();
      return null;
    }
  }

  function handleLegalEntityClicked(entity: LegalEntity): void {
    if (!entity?.id) return;

    const parentPath = new URL(
      ".",
      window.location.origin + window.location.pathname
    );

    openDialog(
      {
        titleStyle: {
          position: "absolute",
          right: 0,
          top: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        component: (
          <EditLegalEntity
            legalEntity={entity}
            onSave={async (updatedEntity: LegalEntity) => {
              const updatedEntityResult = await updateLegalEntity(updatedEntity);
              if (updatedEntityResult) {
                snackbar.enqueueSnackbar(
                  `Legal entity ${updatedEntityResult.name?.value} updated successfully`,
                  { variant: "success" }
                );
                // Dispatch populateLegalEntities to refresh Redux state
                if (session.user?.id) {
                  dispatch(populateLegalEntities({ session, userId: session.user.id }));
                }
              } else {
                snackbar.enqueueSnackbar(
                  `Failed to update legal entity ${updatedEntity.name?.value}`,
                  { variant: "error" }
                );
              }
            }}
          />
        ),
      },
      () => navigate(parentPath.pathname)
    );
  }

  function renderNameColumn(entity: LegalEntity): React.ReactNode {
    return (
      <NameCellContainer>
        <NameText>{entity.name?.value}</NameText>
      </NameCellContainer>

    );
  }

  function renderIsVendorColumn(entity: LegalEntity): React.ReactNode {
    return (
      <span>{entity.isVendor ? "Yes" : "No"}</span>
    );
  }

  function renderIsClientColumn(entity: LegalEntity): React.ReactNode {
    return (
      <span>{entity.isClient ? "Yes" : "No"}</span>
    );
  }

  function renderEmailDomainsColumn(entity: LegalEntity): React.ReactNode {
    if (entity.emailDomains && entity.emailDomains.length > 0) {
      return <span>{entity.emailDomains.map(domain => domain.value).join(", ")}</span>;
    } else {
      return <span><em>No Email Domains</em></span>;
    }
  }

  const entityColumnHelper = createMRTColumnHelper<LegalEntity>();
  let columns = [
    entityColumnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
    }),
    entityColumnHelper.accessor(renderIsVendorColumn, {
      header: "Is Vendor",
      id: "isVendor",
    }),
    entityColumnHelper.accessor(renderIsClientColumn, {
      header: "Is Client",
      id: "isClient",
    }),
    entityColumnHelper.accessor(renderEmailDomainsColumn, {
      header: "Allowed Email Domains",
      id: "emailDomains",
    })
  ];

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: false,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    getRowId: (row) => row.id?.toString() ?? `index-${Guid.generate().toString()}`,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    muiTableContainerProps: ({ table }) => {
      const topToolbarHeight = table.refs.topToolbarRef.current?.offsetHeight ?? 0;
      const bottomToolbarHeight = table.refs.bottomToolbarRef.current?.offsetHeight ?? 0;
      return {
        sx: {
          height: `calc(100% - ${topToolbarHeight}px - ${bottomToolbarHeight}px)`,
        },
      };
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: wasErrorLoading
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading,
      pagination,
      showAlertBanner: wasErrorLoading,
      showProgressBars: isRefetching,
      sorting,
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleLegalEntityClicked(row.original),
      style: { cursor: 'pointer' },
    }),
  });

  return <MaterialReactTable table={table} />;
};

export default LegalEntityAdministrationTable;

